import { useEffect } from "react";

/**
 * Fetches public tracking config and injects client-side pixel scripts
 * (Meta Pixel, TikTok Pixel, GA4 gtag) into <head>.
 */
export function PixelScripts() {
	useEffect(() => {
		fetch("/api/track/config")
			.then((r) => r.json())
			.then((cfg: Record<string, string>) => {
				if (cfg["enabled"] !== "true") return;

				try {
					if (cfg["meta_pixel_id"]) {
						loadMetaPixel(cfg["meta_pixel_id"]);
					}
				} catch (e) {
					console.error("[pixel] Meta Pixel load failed:", e);
				}

				try {
					if (cfg["tiktok_pixel_id"]) {
						loadTikTokPixel(cfg["tiktok_pixel_id"]);
					}
				} catch (e) {
					console.error("[pixel] TikTok Pixel load failed:", e);
				}

				try {
					if (cfg["ga4_measurement_id"]) {
						loadGA4(cfg["ga4_measurement_id"]);
					}
				} catch (e) {
					console.error("[pixel] GA4 load failed:", e);
				}
			})
			.catch((e) => {
				console.error("[pixel] Failed to fetch tracking config:", e);
			});
	}, []);

	return null;
}

function loadMetaPixel(pixelId: string) {
	if (document.getElementById("meta-pixel-script")) return;
	// prettier-ignore
	(function(f: any, b: Document, e: string, v: string, n?: any, t?: any, s?: any) {
		if (f.fbq) return;
		n = f.fbq = function() {
			n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
		};
		if (!f._fbq) f._fbq = n;
		n.push = n;
		n.loaded = !0;
		n.version = "2.0";
		n.queue = [];
		t = b.createElement(e);
		t.id = "meta-pixel-script";
		t.async = !0;
		t.src = v;
		s = b.getElementsByTagName(e)[0];
		s.parentNode!.insertBefore(t, s);
	})(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
	window.fbq("init", pixelId);
	window.fbq("track", "PageView");
}

function loadTikTokPixel(pixelCode: string) {
	if (document.getElementById("tiktok-pixel")) return;
	const w = window as any;
	if (!w.TiktokAnalyticsObject) {
		w.TiktokAnalyticsObject = "ttq";
		const ttq: any = (w[w.TiktokAnalyticsObject] = w[w.TiktokAnalyticsObject] || []);
		ttq.methods = ["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];
		ttq.t = +new Date;
		ttq.load = function(e: string) {
			const i = document.createElement("script");
			i.type = "text/javascript";
			i.id = "tiktok-pixel";
			i.async = true;
			i.src = "https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=" + e + "&lib=ttq";
			const s = document.getElementsByTagName("script")[0];
			s.parentNode!.insertBefore(i, s);
		};
	}
	if (typeof w.ttq === "function") {
		w.ttq.load(pixelCode);
	} else if (Array.isArray(w.ttq)) {
		w.ttq.push(["load", pixelCode]);
		w.ttq.load(pixelCode);
	}
}

function loadGA4(measurementId: string) {
	if (document.getElementById("ga4-gtag")) return;
	const s = document.createElement("script");
	s.id = "ga4-gtag";
	s.async = true;
	s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
	document.head.appendChild(s);

	const w = window as any;
	const dataLayer = w.dataLayer || (w.dataLayer = []);
	function gtag(...args: any[]) { dataLayer.push(args); }
	w.gtag = gtag;
	gtag("js", new Date());
	gtag("config", measurementId, { send_page_view: false });
}

// Extend Window for pixel globals
declare global {
	interface Window {
		fbq: (...args: any[]) => void;
		ttq: (...args: any[]) => void;
		gtag: (...args: any[]) => void;
	}
}
